/**
 * StripePaymentElement — the real card-capture surface, ported from the
 * prototype's ``StripeCardForm`` but backed by ``@stripe/react-stripe-js``
 * ``<PaymentElement>``.
 *
 * Card data ONLY ever touches Stripe Elements — it is never read into React
 * state, never logged, and never sent to our backend. On submit we confirm a
 * **SetupIntent** (no charge) and hand the resulting (non-sensitive)
 * ``payment_method_id`` back to the caller; the backend re-derives the
 * authoritative ``card_on_file`` (brand/last4) from that payment method.
 *
 * MUST be rendered inside an ``<Elements>`` provider whose ``options`` carry
 * the SetupIntent ``clientSecret`` (see ``CheckoutPanel``).
 *
 * A11y: the submit button keeps a ≥44px tap target; the field's own focus ring
 * is provided by Stripe. The "Secured by Stripe" footer reassures the parent.
 *
 * The button stays live on an unfinished form: a tap has Stripe mark what is
 * missing and puts the cursor there, so a parent is never left at a grey button.
 */

import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { ChromeTokens } from "../chrome/tokens";
import { CardSubmitButton } from "./CardSubmitButton";
import { StripeSecured } from "./StripeSecured";
import type { CardCaptureResult } from "./types";

export interface StripePaymentElementProps {
  /** Called with the confirmed payment-method id (and best-effort brand/last4). */
  onComplete: (card: CardCaptureResult) => void | Promise<void>;
  /** Label for the submit button (e.g. "Start my 7-day free trial"). */
  submitLabel: string;
  /** When true, the parent is mid-processing (post-confirm billing call). */
  busy?: boolean;
  /** Where the submit button goes when the sheet pins it; in the form otherwise. */
  footer?: HTMLElement | null;
}

function extractPaymentMethodId(pm: unknown): string {
  if (typeof pm === "string") return pm;
  if (pm && typeof pm === "object" && "id" in pm) {
    return String((pm as { id: unknown }).id);
  }
  return "";
}

export function StripePaymentElement({ onComplete, submitLabel, busy = false, footer = null }: StripePaymentElementProps): JSX.Element {
  const stripe = useStripe();
  const elements = useElements();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [complete, setComplete] = useState(false);
  const form = useRef<HTMLFormElement>(null);
  // State reaches the button on the next render; a second tap can land before it.
  const inFlight = useRef(false);

  const disabled = !stripe || !elements || confirming || busy;

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!stripe || !elements || inFlight.current) return;
    if (!complete) {
      setError(null);
      await elements.submit();
      form.current?.scrollIntoView?.({ block: "center" });
      elements.getElement("payment")?.focus();
      return;
    }
    inFlight.current = true;
    setConfirming(true);
    setError(null);
    try {
      const result = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      });
      if (result.error) {
        setError(result.error.message ?? "We couldn't save that card. Please try again.");
        return;
      }
      const pmId = extractPaymentMethodId(result.setupIntent?.payment_method);
      if (!pmId) {
        setError("We couldn't save that card. Please try again.");
        return;
      }
      // brand/last4 are intentionally omitted — the backend re-derives the
      // authoritative card_on_file from the payment method.
      await onComplete({ payment_method_id: pmId });
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      inFlight.current = false;
      setConfirming(false);
    }
  };

  const button = (
    <CardSubmitButton label={submitLabel} waiting={confirming || busy} disabled={disabled} onClick={handleSubmit} />
  );

  return (
    <form ref={form} onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: ChromeTokens.spacing.md }}>
      <div
        style={{
          borderRadius: ChromeTokens.radius.md,
          border: `1.5px solid ${ChromeTokens.color.baseMuted}`,
          padding: ChromeTokens.spacing.md,
          background: ChromeTokens.color.base,
        }}
      >
        <PaymentElement options={{ layout: "tabs" }} onChange={(e) => setComplete(e.complete)} />
      </div>

      {error && (
        <p
          role="alert"
          style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: ChromeTokens.color.danger, lineHeight: 1.4 }}
        >
          {error}
        </p>
      )}

      {!footer && button}
      {footer && createPortal(button, footer)}
      <StripeSecured />
    </form>
  );
}
